import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  DEAL_STAGES as FALLBACK_STAGES,
  SUBSCRIPTION_PLANS as FALLBACK_PLANS,
  SERVICE_GROUPS as FALLBACK_GROUPS,
} from "@/lib/dealStages";

/**
 * PipelineOptionsProvider — loads status / plan / service_group option lists
 * from the backend once on mount and re-fetches when `refresh()` is called
 * (e.g. after the admin adds/renames/deletes an option via Settings).
 *
 * Consumers use `usePipelineOptions()` to read the current lists and the
 * `getStageMeta` / `getPlanMeta` helpers so badges + dropdowns stay in sync
 * with the admin's edits, without every component reloading manually.
 */

const FALLBACK_STAGE_ITEMS = FALLBACK_STAGES.map((s, i) => ({
  slug: s.value,
  label: s.label,
  badge: s.color,
  dot: s.dot,
  locked: s.value === "won" || s.value === "lost",
  order: i,
}));

const FALLBACK_PLAN_ITEMS = FALLBACK_PLANS.map((p, i) => ({
  slug: p.value,
  label: p.label,
  locked: false,
  order: i,
}));

const FALLBACK_GROUP_ITEMS = FALLBACK_GROUPS.map((sg, i) => ({
  slug: sg,
  label: sg,
  locked: false,
  order: i,
}));

const PipelineOptionsContext = createContext(null);

export function PipelineOptionsProvider({ children }) {
  const [status, setStatus] = useState(FALLBACK_STAGE_ITEMS);
  const [plan, setPlan] = useState(FALLBACK_PLAN_ITEMS);
  const [serviceGroup, setServiceGroup] = useState(FALLBACK_GROUP_ITEMS);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/site/pipeline-options");
      if (Array.isArray(data.status) && data.status.length) setStatus(data.status);
      if (Array.isArray(data.plan) && data.plan.length) setPlan(data.plan);
      if (Array.isArray(data.service_group) && data.service_group.length) setServiceGroup(data.service_group);
      setLoaded(true);
    } catch {
      /* keep fallback */
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => {
    const stagesForUi = status.map((s) => ({
      value: s.slug,
      label: s.label,
      color: s.badge,
      dot: s.dot,
      locked: !!s.locked,
    }));
    const plansForUi = plan.map((p) => ({ value: p.slug, label: p.label, locked: !!p.locked }));
    const groupsForUi = serviceGroup.map((g) => g.slug); // string list for existing consumers
    const getStageMeta = (slug) => stagesForUi.find((s) => s.value === slug) || stagesForUi[0];
    const getPlanMeta = (slug) => plansForUi.find((p) => p.value === slug);
    return {
      stages: stagesForUi,
      plans: plansForUi,
      serviceGroups: groupsForUi,
      rawStatus: status,
      rawPlan: plan,
      rawServiceGroup: serviceGroup,
      getStageMeta,
      getPlanMeta,
      refresh,
      loaded,
    };
  }, [status, plan, serviceGroup, refresh, loaded]);

  return <PipelineOptionsContext.Provider value={value}>{children}</PipelineOptionsContext.Provider>;
}

export function usePipelineOptions() {
  const ctx = useContext(PipelineOptionsContext);
  if (!ctx) {
    // Safe fallback for tests / storybook renders without a provider
    return {
      stages: FALLBACK_STAGES,
      plans: FALLBACK_PLANS,
      serviceGroups: FALLBACK_GROUPS,
      rawStatus: FALLBACK_STAGE_ITEMS,
      rawPlan: FALLBACK_PLAN_ITEMS,
      rawServiceGroup: FALLBACK_GROUP_ITEMS,
      getStageMeta: (slug) => FALLBACK_STAGES.find((s) => s.value === slug) || FALLBACK_STAGES[0],
      getPlanMeta: (slug) => FALLBACK_PLANS.find((p) => p.value === slug),
      refresh: async () => {},
      loaded: true,
    };
  }
  return ctx;
}
